import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Form, Button, Card } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import axios from 'axios';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { FaFilePdf } from 'react-icons/fa';
import excelIcon from '../../assets/icons/excel.png';
import { Tooltip } from "@mui/material";
import AppDataGrid from '../../components/common/AppDataGrid'; // ⬅️ switched to your AppDataGrid

const TYPE_LABELS = { A: "Attendance", H: "Homework", HR: "Homework Review" };

// MUI DataGrid columns builder (matches your Role/Status example style)
const buildColumns = (form) => {
  const cols = [
    {
      field: "class_name",
      headerName: "Class",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
    {
      field: "section_name",
      headerName: "Section",
      flex: 1,
      minWidth: 120,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    },
  ];

  if (form?.type === "H" || form?.type === "HR") {
    cols.push({
      field: "subject_name",
      headerName: "Subject",
      flex: 1.2,
      minWidth: 150,
      headerAlign: "center",
      align: "center",
      sortable: true,
      renderCell: (params) => (
        <Tooltip title={params.value ?? ""}>
          <span>{params.value ?? ""}</span>
        </Tooltip>
      ),
    });
  }

  cols.push({
    field: "subject_teacher",
    headerName: form?.type === "H" || form?.type === "HR" ? "Subject Teacher" : "Class Teacher",
    flex: 1.4,
    minWidth: 170,
    headerAlign: "center",
    align: "center",
    sortable: true,
    renderCell: (params) => {
      const label = form?.type === "H" || form?.type === "HR" ? "Subject Teacher" : "Class Teacher";
      const value = params.value ?? "";
      return (
        <Tooltip title={`${label}: ${value}`}>
          <span>{value}</span>
        </Tooltip>
      );
    },
  });

  cols.push({
    field: "task_type",
    headerName: "Type",
    flex: 1,
    minWidth: 130,
    headerAlign: "center",
    align: "center",
    sortable: true,
    valueGetter: (params) => TYPE_LABELS[params.row?.task_type] ?? (params.row?.task_type ?? ""),
    renderCell: (params) => (
      <Tooltip title={params.value ?? ""}>
        <span>{params.value ?? ""}</span>
      </Tooltip>
    ),
  });

  cols.push({
    field: "status",
    headerName: "Status",
    flex: 1,
    minWidth: 120,
    headerAlign: "center",
    align: "center",
    sortable: true,
    renderCell: (params) => (
      <Tooltip title={params.value ?? ""}>
        <span>{params.value ?? ""}</span>
      </Tooltip>
    ),
  });

  return cols;
};

const MonitoringReport = () => {
  const userData = sessionStorage.getItem('user');
  const userObj = JSON.parse(userData);
  const baseUrl = process.env.REACT_APP_API_BASE_URL;

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [reportData, setReportData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [showTable, setShowTable] = useState(false);
  const [form, setForm] = useState({
    class_id: '',
    section_id: '',
    status: '',
    type: '',
    date: new Date().toISOString().split("T")[0], // default to today
  });

  useEffect(() => {
    fetchClasses();
  }, []);

  useEffect(() => {
    setShowTable(false);
    setReportData([]);
  }, [form.type]);

  useEffect(() => {
    if (form.class_id) fetchSections(form.class_id);
  }, [form.class_id]);

  const fetchClasses = async () => {
    try {
      const res = await axios.post(baseUrl + '/classes/', {
        action: 'READ',
        school_id: userObj.school_id
      });
      setClasses(res.data || []);
    } catch (err) {
      console.error('Error fetching classes:', err);
    }
  };

  const fetchSections = async (class_id) => {
    try {
      const res = await axios.post(baseUrl + '/Sections/', {
        action: 'DROPDOWNREAD',
        class_id,
        school_id: userObj.school_id,
        academic_year_id: userObj.academic_year_id,
      });
      setSections(res.data || []);
    } catch (err) {
      console.error('Error fetching sections:', err);
    }
  };

  const handleInputChange = (e) => {
    const { id, value } = e.target;
    setForm(prev => ({ ...prev, [id]: value ?? '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${baseUrl}/getstatus/`, {
        input_date: form.date,
        input_type: form.type,
        input_academic_year_id: userObj.academic_year_id,
        input_school_id: userObj.school_id,
        input_class_id: form.class_id || null,
        input_section_id: form.section_id || null,
        input_status_filter: form.status || null
      });

      const data = res.data || [];
      setShowTable(true);

      if (data.length === 0) {
        setReportData([]); // no rows -> DataGrid will show "No Records Found" overlay
        return;
      }

      setReportData(data);
    } catch (err) {
      toast.error("Something went wrong!");
      console.error("Monitoring Report Error:", err);
    }
  };

  const getExportData = () => {
    const typeMap = TYPE_LABELS;

    const columns = [
      "Date",
      "Class",
      "Section",
      (form.type === 'H' || form.type === 'HR') ? "Subject Teacher" : "Class Teacher"
    ];

    if (form.type === 'H' || form.type === 'HR') {
      columns.push("Subject");
    }

    columns.push("Type", "Status");

    const rows = reportData.map(row => {
      const rowData = [
        row.task_date || '',
        row.class_name || '',
        row.section_name || '',
        row.subject_teacher || ''
      ];

      if (form.type === 'H' || form.type === 'HR') {
        rowData.push(row.subject_name || '');
      }

      rowData.push(typeMap[row.task_type] || row.task_type || '');
      rowData.push(row.status || '');

      return rowData;
    });

    return { columns, rows };
  };

  const exportToPDF = () => {
    if (!showTable || reportData.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const { columns, rows } = getExportData();

    const doc = new jsPDF();
    doc.setFontSize(14);
    doc.text("Monitoring Report", 14, 15);

    autoTable(doc, {
      startY: 20,
      head: [columns],
      body: rows,
      styles: { fontSize: 9 },
      headStyles: { fillColor: [41, 128, 185] }
    });

    doc.save("Monitoring_Report.pdf");
  };

  const exportToExcel = () => {
    if (!showTable || reportData.length === 0) {
      toast.error("No data available to export.");
      return;
    }

    const { columns, rows } = getExportData();

    const formatted = rows.map(row => {
      const obj = {};
      columns.forEach((col, index) => {
        obj[col] = row[index];
      });
      return obj;
    });

    const ws = XLSX.utils.json_to_sheet(formatted);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "MonitoringReport");
    XLSX.writeFile(wb, "Monitoring_Report.xlsx");
  };

  // Text filter (kept same behavior)
  const filteredData = reportData.filter(item =>
    Object.values(item).join(' ').toLowerCase().includes(searchText.toLowerCase())
  );

  // Robust row id (if your API doesn't return a unique id)
  const getRowId = (row) =>
    row.id ??
    row.task_id ??
    `${row.task_date || ""}-${row.class_name || ""}-${row.section_name || ""}-${row.subject_name || ""}-${row.task_type || ""}`;

  return (
    <div className='pageBody'>
      <ToastContainer />
      <Container fluid>
        <Card>
          <Card.Body className="hide-scrollbar">
            <form onSubmit={handleSubmit}>
              <Row>
                <Col xs={12}>
                  <h6 className='commonSectionTitle'>Monitoring Report</h6>
                </Col>
              </Row>
              <Row>
                <Col xs={12} md={6} lg={2}>
                  <Form.Group className="commonInput">
                    <Form.Label>Date</Form.Label>
                    <Form.Control type='date' id='date' value={form.date} onChange={handleInputChange} />
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={2}>
                  <Form.Group className="commonInput">
                    <Form.Label>Class</Form.Label>
                    <Form.Select id='class_id' value={form.class_id} onChange={handleInputChange}>
                      <option value=''  disabled hidden>Select Class</option>
                      {classes.map(cls => (
                        <option key={cls.class_id} value={cls.class_id}>{cls.class_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={2}>
                  <Form.Group className="commonInput">
                    <Form.Label>Section</Form.Label>
                    <Form.Select id='section_id' value={form.section_id} onChange={handleInputChange}>
                      <option value=''  disabled hidden>Select Section</option>
                      {sections.map(sec => (
                        <option key={sec.section_id} value={sec.section_id}>{sec.section_name}</option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={2}>
                  <Form.Group className="commonInput">
                    <Form.Label>Status</Form.Label>
                    <Form.Select id='status' value={form.status} onChange={handleInputChange}>
                      <option value=''>Select Status</option>
                      <option value='Yes'>Yes</option>
                      <option value='No'>No</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col xs={12} md={6} lg={2}>
                  <Form.Group className="commonInput">
                    <Form.Label>Type<span className="requiredStar">*</span></Form.Label>
                    <Form.Select required id='type' value={form.type} onChange={handleInputChange}>
                      <option value=''>Select Type</option>
                      <option value='A'>Attendance</option>
                      <option value='H'>Homework</option>
                      <option value='HR'>Homework Review</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <div className="d-flex justify-content-end mt-4">
                    <Button
                      type="button"
                      variant="primary"
                      className="btn-info clearBtn me-3"
                      onClick={() => {
                        setForm({ class_id: '', section_id: '', status: '', type: '', date: new Date().toISOString().split("T")[0] });
                        setReportData([]);
                        setShowTable(false);
                      }}
                    >
                      Reset
                    </Button>
                    <Button
                      type="submit"
                      variant="primary"
                      className="btn-success primaryBtn"
                    >
                      Search
                    </Button>
                  </div>
                </Col>
              </Row>
            </form>
          </Card.Body>
        </Card>

        
          <>
            <Row className='mt-3 mb-2 align-items-center'>
              <Col md={3}>
                <Form.Control
                  type="text"
                  placeholder="Search..."
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                />
              </Col>
              <Col className="d-flex justify-content-end">
                <Button variant="success" onClick={exportToExcel} className="me-2">
                  <img src={excelIcon} alt="excel" style={{ width: 20, marginRight: 5 }} />
                </Button>
                <Button variant="danger" onClick={exportToPDF}>
                  <FaFilePdf style={{ marginRight: 5 }} />
                </Button>
              </Col>
            </Row>

            <div className="commonTable mt-2">
              <AppDataGrid
                rows={filteredData.length ? filteredData : []}
                columns={buildColumns(form)}
                getRowId={getRowId}
              pageSizeOptions={[10, 20, 50]} 
                showToolbar={false}
                pagination
                density="compact"
                maxHeight={400}
                slots={{ noRowsOverlay: () => <div style={{ padding: 16 }}>No Records Found</div> }}
              />
            </div>
          </>
        
      </Container>
    </div>
  );
};

export default MonitoringReport;
